import React from 'react';
import {
  freeForUsersFact,
  mintingExample,
  monthlyPrice,
  noTransactionFeesFact,
  ownPosHeadline,
  primaryDemoCta,
  tryCouplingCta,
} from '../../content/siteMessaging';
import { useBooking } from '../BookingProvider';

export const PricingFactsSection: React.FC = () => {
  const booking = useBooking();
  const merchantPlanHighlights = [
    freeForUsersFact,
    noTransactionFeesFact,
    ownPosHeadline,
    'Catalogs, online orders, appointments, subscriptions, and tip pooling',
    'Merchant-controlled rewards, vouchers, and gift cards',
  ];

  return (
    <section
      id="pricing"
      className="relative overflow-hidden bg-[#fff7ef] px-4 py-24 text-neutral-900"
    >
      <div
        className="absolute inset-x-0 top-0 h-48"
        style={{
          background:
            'radial-gradient(circle at top, rgba(230, 147, 79, 0.22), transparent 70%)',
        }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-x-0 bottom-0 h-28"
        style={{
          background:
            'linear-gradient(to top, rgba(224, 188, 150, 0.2), rgba(224, 188, 150, 0))',
        }}
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-6xl">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-flex rounded-full bg-white px-4 py-1 text-sm font-semibold uppercase tracking-[0.18em] text-brand-primary shadow-sm">
            Transparent pricing
          </span>
          <h2 className="mt-6 text-4xl font-bold tracking-tight sm:text-5xl">
            Simple tools. Predictable costs.
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-neutral-600">
            No monthly subscription dues and no per-sale Coupling fee. Token
            minting and ordinary card-processor costs remain separate.
          </p>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-2">
          <article className="rounded-[2rem] border border-brand-primary/12 bg-white p-8 shadow-[0_24px_60px_rgba(130,64,29,0.12)]">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-brand-primary">
              Merchant access
            </p>
            <div className="mt-4 text-5xl font-bold tracking-tight text-neutral-900">
              {monthlyPrice}
            </div>
            <p className="mt-3 max-w-md text-base leading-relaxed text-neutral-600">
              Run checkout, appointments, and rewards with no monthly
              subscription dues and no Coupling fee on each redemption.
            </p>

            <div className="mt-8 grid gap-3">
              {merchantPlanHighlights.map((fact) => (
                <div
                  key={fact}
                  className="flex items-center gap-3 rounded-2xl border border-brand-primary/10 bg-[#fffaf4] px-4 py-3 text-sm font-medium text-neutral-800"
                >
                  <span className="inline-flex h-5 w-5 flex-none items-center justify-center rounded-full border border-[#e3b184] bg-white text-[0.7rem] text-brand-primary">
                    +
                  </span>
                  <span>{fact}</span>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={() => booking.open('demo')}
              className="mt-8 inline-flex w-full items-center justify-center rounded-[1.1rem] bg-[#f0b748] px-6 py-4 text-base font-semibold text-[#3d210f] shadow-[0_18px_35px_rgba(240,183,72,0.24)] transition-transform hover:-translate-y-0.5"
            >
              {primaryDemoCta}
            </button>
          </article>

          <article
            className="rounded-[2rem] border border-[#c96f40]/30 p-8 text-white shadow-[0_24px_60px_rgba(130,64,29,0.18)]"
            style={{
              background:
                'linear-gradient(135deg, #c86234 0%, #a84c26 52%, #73331c 100%)',
            }}
          >
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-white/22 bg-white/10 text-white">
              <span className="text-xl">$</span>
            </div>
            <p className="mt-5 text-sm font-semibold uppercase tracking-[0.2em] text-[#ffe0b7]">
              Reward issuance cost
            </p>
            <h3 className="mt-5 max-w-xl text-4xl font-bold tracking-tight text-white sm:text-5xl">
              Issue <span className="text-[#ffe8c4]">$10,000</span> in rewards for
              about <span className="text-[#ffe8c4]">$1.20</span>.
            </h3>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-white/86">
              Minting costs apply when you issue tokens and are shown before
              confirmation. Ordinary card-processor fees may also apply when
              customers pay by card.
            </p>

            <div className="mt-8 rounded-[1.5rem] border border-white/14 bg-white/10 px-5 py-4 backdrop-blur-sm">
              <p className="text-sm font-medium leading-relaxed text-white/90">
                {mintingExample}
              </p>
            </div>
          </article>
        </div>

        <div className="mt-6 rounded-[2rem] border border-brand-primary/12 bg-[#fff1e1] px-6 py-6 shadow-[0_18px_45px_rgba(130,64,29,0.10)] sm:px-8">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-lg font-semibold text-neutral-900">
                Need help planning a rollout or merchant network?
              </p>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-neutral-700">
                {primaryDemoCta} and we&apos;ll walk through your current
                checkout flow, network goals, and what the pricing looks like
                for your shop.
              </p>
            </div>

            <a
              href="/contact"
              className="inline-flex items-center justify-center rounded-full border border-brand-primary/15 bg-white px-6 py-3 text-sm font-semibold text-brand-primary shadow-sm transition-colors hover:bg-[#fff8f1]"
            >
              {tryCouplingCta}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingFactsSection;
